/**
 * A lesson's text as its clients draw it (gh #4872 owner-delegated decisions 34 and 35): the
 * tree the backend parses from the lesson's Markdown once, when the lesson is saved, and stores
 * with it, so no client parses org-authored Markdown (micromark parses several shapes
 * super-linearly, which froze a client's JS thread for seconds: wayai#4935).
 *
 * THE TREE is a closed subset of mdast (the syntax tree `react-markdown` and the mobile app's
 * renderer already draw), with no `position` and no `data`: every node type and field is below,
 * and nothing else ever appears. The parse is GitHub-flavored Markdown (tables, strikethrough,
 * task lists, autolinks, footnotes), and then:
 *   - RAW HTML is dropped, a block or inline tag alike: nothing in the tree is HTML.
 *   - LINE BREAKS: a line ending inside a paragraph, a heading or a table cell is a `break` node,
 *     as `remark-breaks` draws it; no `text` value holds a line ending, and an `inlineCode` value's
 *     line endings are spaces (CommonMark's rule). Only a `code` block's value keeps them.
 *   - LINKS are resolved: a reference (`[text][label]`) is a `link` carrying its definition's URL
 *     and title, and definitions are gone. A link's `url` is always an absolute URL of one of
 *     `COMMUNITY_LESSON_LINK_PROTOCOLS`, percent-encoded as `react-markdown` encodes an `href`; a
 *     link to anything else — `javascript:`, `data:`, a relative URL, which means nothing to a
 *     client with no base — is its text, unlinked. No link is inside another.
 *   - AN IMAGE is a link to it whose text is its alt text, or its URL when it has none (under the
 *     same rule; past it, its alt text alone), and inside a link its alt text alone. No client
 *     loads a remote image from a lesson: a lesson is read by every member, and an image would
 *     tell its host who opened it and when. As a link, nothing its author wrote is lost.
 *   - FOOTNOTES: a `footnoteReference` names a `footnoteDefinition` the tree holds; the
 *     definitions are at the end of the tree's `children`, in the order of their first reference,
 *     and a definition nothing references is dropped. A client numbers them by that order.
 *   - BOUNDS: at most `COMMUNITY_LESSON_TREE_MAX_DEPTH` levels below the root, and at most
 *     `COMMUNITY_LESSON_TREE_MAX_BYTES` of JSON, so a client recurses a bounded depth. A save whose
 *     text exceeds either is refused (400, `details.reason`: `COMMUNITY_LESSON_TREE_TOO_DEEP_REASON`
 *     or `COMMUNITY_LESSON_TREE_TOO_LARGE_REASON`), and so, before it is parsed, is one with a line
 *     past `COMMUNITY_LESSON_CONTAINER_PREFIX_MAX_COLUMNS` (`COMMUNITY_LESSON_LINE_PREFIX_TOO_WIDE_REASON`).
 *
 * READING: `CommunityLesson.body_tree`, beside the Markdown (`body`) it was parsed from. A lesson
 * stores the `COMMUNITY_LESSON_TREE_VERSION` its tree was parsed under; a read of one stored under
 * an older version — or none, a lesson saved before trees were stored — parses it again first, and
 * stores that. A text saved before these bounds existed that exceeds one reads as its Markdown
 * as written, in one `code` block.
 */

// =============================================================================
// LIMITS
// =============================================================================

/**
 * The most bytes a lesson's tree takes as JSON (UTF-8). A lesson read returns one tree, and the
 * row that stores it stays within the storage's 2 MB row limit with its Markdown and links.
 */
export const COMMUNITY_LESSON_TREE_MAX_BYTES = 512 * 1024;

/** The most levels a node of a lesson's tree sits below its root. */
export const COMMUNITY_LESSON_TREE_MAX_DEPTH = 32;

/**
 * The most columns a line of a lesson's text may start with in indentation and the markers that
 * open block quotes, list items and footnotes (`>`, `-`, `+`, `*`, `1.`, `1)`, `[^label]:`), up to
 * its last marker, a tab counting to the next multiple of 4. A text with a line past it is refused
 * before it is parsed: containers nested that deep are what costs the parse the most. The count
 * reads markers, not what the line becomes, so a line that opens nothing — a thematic break of
 * more than 50 spaced markers, a code line that starts with 51 `> ` — is refused too.
 */
export const COMMUNITY_LESSON_CONTAINER_PREFIX_MAX_COLUMNS = 100;

/** The URL schemes a lesson's link takes: what web's sanitizer allows and every client opens. */
export const COMMUNITY_LESSON_LINK_PROTOCOLS = ['http', 'https', 'mailto'] as const;

/**
 * The version of the rules above (the node set, what is dropped, the bounds). Raising it re-derives
 * every stored tree on its lesson's next read.
 */
export const COMMUNITY_LESSON_TREE_VERSION = 1;

/** The `details.reason` of the 400 a save gets when its text's tree exceeds `COMMUNITY_LESSON_TREE_MAX_BYTES`. */
export const COMMUNITY_LESSON_TREE_TOO_LARGE_REASON = 'lesson_tree_too_large';
/** The `details.reason` of the 400 a save gets when its text nests deeper than `COMMUNITY_LESSON_TREE_MAX_DEPTH`. */
export const COMMUNITY_LESSON_TREE_TOO_DEEP_REASON = 'lesson_tree_too_deep';
/**
 * The `details.reason` of the 400 a save gets when a line of its text — `details.line`, from 1 —
 * starts past `COMMUNITY_LESSON_CONTAINER_PREFIX_MAX_COLUMNS`.
 */
export const COMMUNITY_LESSON_LINE_PREFIX_TOO_WIDE_REASON = 'lesson_line_prefix_too_wide';

const LINK_SCHEME = new RegExp(`^(${COMMUNITY_LESSON_LINK_PROTOCOLS.join('|')}):`, 'i');

/**
 * `url` as a lesson's link keeps it: with its scheme lowercased when it is one of
 * `COMMUNITY_LESSON_LINK_PROTOCOLS`, followed by its colon; null for any other URL (another
 * scheme, a relative URL). `url` is already percent-encoded, so no character before its colon
 * can disguise a scheme.
 */
export function communityLessonLinkUrl(url: string): string | null {
  const scheme = LINK_SCHEME.exec(url)?.[1];
  return scheme ? scheme.toLowerCase() + url.slice(scheme.length) : null;
}

// =============================================================================
// THE TREE
// =============================================================================

/** A lesson's text: its blocks, then its footnotes' definitions. */
export interface CommunityLessonTree {
  type: 'root';
  children: CommunityLessonTreeBlock[];
}

/** A node that stands on its own lines. */
export type CommunityLessonTreeBlock =
  | CommunityLessonTreeParagraph
  | CommunityLessonTreeHeading
  | CommunityLessonTreeThematicBreak
  | CommunityLessonTreeBlockquote
  | CommunityLessonTreeList
  | CommunityLessonTreeCode
  | CommunityLessonTreeTable
  | CommunityLessonTreeFootnoteDefinition;

/** A node inside a line: a paragraph's, a heading's or a table cell's. */
export type CommunityLessonTreeInline =
  | CommunityLessonTreeText
  | CommunityLessonTreeEmphasis
  | CommunityLessonTreeStrong
  | CommunityLessonTreeDelete
  | CommunityLessonTreeInlineCode
  | CommunityLessonTreeBreak
  | CommunityLessonTreeLink
  | CommunityLessonTreeFootnoteReference;

/** Every node a lesson's tree holds. */
export type CommunityLessonTreeNode =
  | CommunityLessonTree
  | CommunityLessonTreeBlock
  | CommunityLessonTreeListItem
  | CommunityLessonTreeTableRow
  | CommunityLessonTreeTableCell
  | CommunityLessonTreeInline;

export interface CommunityLessonTreeParagraph {
  type: 'paragraph';
  children: CommunityLessonTreeInline[];
}

export interface CommunityLessonTreeHeading {
  type: 'heading';
  depth: 1 | 2 | 3 | 4 | 5 | 6;
  children: CommunityLessonTreeInline[];
}

export interface CommunityLessonTreeThematicBreak {
  type: 'thematicBreak';
}

export interface CommunityLessonTreeBlockquote {
  type: 'blockquote';
  children: CommunityLessonTreeBlock[];
}

export interface CommunityLessonTreeList {
  type: 'list';
  ordered: boolean;
  /** An ordered list's first number; null for a bulleted one. */
  start: number | null;
  /** Its items are separated by blank lines (drawn apart), or not. */
  spread: boolean;
  children: CommunityLessonTreeListItem[];
}

export interface CommunityLessonTreeListItem {
  type: 'listItem';
  /** A task's box: ticked, not ticked, or null for an item that is no task. */
  checked: boolean | null;
  spread: boolean;
  children: CommunityLessonTreeBlock[];
}

/** A fenced or indented code block: its text as written, line endings kept. */
export interface CommunityLessonTreeCode {
  type: 'code';
  /** The fence's language word; null for none. */
  lang: string | null;
  value: string;
}

export interface CommunityLessonTreeTable {
  type: 'table';
  /** Each column's alignment; null for none. */
  align: Array<'left' | 'right' | 'center' | null>;
  /** Its header row first. */
  children: CommunityLessonTreeTableRow[];
}

export interface CommunityLessonTreeTableRow {
  type: 'tableRow';
  children: CommunityLessonTreeTableCell[];
}

export interface CommunityLessonTreeTableCell {
  type: 'tableCell';
  children: CommunityLessonTreeInline[];
}

/** A footnote's text, at the end of the tree; `identifier` is what its references name. */
export interface CommunityLessonTreeFootnoteDefinition {
  type: 'footnoteDefinition';
  identifier: string;
  /** The label as written. */
  label: string;
  children: CommunityLessonTreeBlock[];
}

/** Text, with no line ending (see `CommunityLessonTreeBreak`). */
export interface CommunityLessonTreeText {
  type: 'text';
  value: string;
}

export interface CommunityLessonTreeEmphasis {
  type: 'emphasis';
  children: CommunityLessonTreeInline[];
}

export interface CommunityLessonTreeStrong {
  type: 'strong';
  children: CommunityLessonTreeInline[];
}

/** Struck-through text. */
export interface CommunityLessonTreeDelete {
  type: 'delete';
  children: CommunityLessonTreeInline[];
}

export interface CommunityLessonTreeInlineCode {
  type: 'inlineCode';
  value: string;
}

/** A line break. */
export interface CommunityLessonTreeBreak {
  type: 'break';
}

/** A link: `url` per `communityLessonLinkUrl`; its children hold no link. */
export interface CommunityLessonTreeLink {
  type: 'link';
  url: string;
  title: string | null;
  children: CommunityLessonTreeInline[];
}

/** A reference to the `footnoteDefinition` of the same `identifier`. */
export interface CommunityLessonTreeFootnoteReference {
  type: 'footnoteReference';
  identifier: string;
  label: string;
}
